import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useOrganization } from '@/hooks/useOrganization';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/hooks/useAuthApi';
import type {
  OrgAskRequest,
  OrgAskResponse,
  OrgAskEvidenceItem,
  OrgAskSessionEvidence,
} from '@/lib/api';
import ReactMarkdown from 'react-markdown';
import { Loader2, Sparkles, ArrowUpRight, Wand2, Search } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  buildClaudeOptions,
  GENERIC_STREAM_MESSAGES,
  prepareResponseContent,
  PROGRESS_HEADLINES,
  PROGRESS_SUBLINES,
  streamAskResponse,
  type StreamProgress,
  type ToolResultEvent,
  type ToolUseEvent,
} from '@/lib/orgAskStream';
import {
  askHobbesCache,
  cloneEntries,
  type AskHobbesEntry,
} from '@/lib/askHobbesCache';
import { format } from 'date-fns';

// Use the shared entry type
type AskEntry = AskHobbesEntry;

const promptPresets = [
  "Show me where we're losing the most users this week.",
  "What are the top three trends I should know about?",
  "Which intents correlate with unresolved outcomes right now?"
];
const tonePresets: Array<{ value: string; label: string; systemPrompt: string }> = [
  {
    value: 'balanced',
    label: 'Balanced',
    systemPrompt: 'You are Hobbes, an expert SaaS product analyst. Provide balanced, actionable answers with clear structure and bullet points.'
  },
  {
    value: 'executive',
    label: 'Executive',
    systemPrompt: 'Respond as a chief customer officer giving an executive summary. Be concise, focus on outcomes, and recommend next actions.'
  },
  {
    value: 'deep-dive',
    label: 'Deep dive',
    systemPrompt: 'Act like an investigative analyst. Provide detailed insights, cite specific sessions, and call out anomalies or trends.'
  }
];


const formatEvidenceDate = (value?: string | null) => {
  if (!value) return undefined;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return format(parsed, 'MMM d, yyyy h:mm a');
};

const formatMetricValue = (value: unknown) => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value % 1 === 0 ? value.toString() : value.toFixed(2);
  }
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return numeric % 1 === 0 ? numeric.toString() : numeric.toFixed(2);
  }
  return String(value);
};

const renderEvidenceCard = (item: OrgAskEvidenceItem, index: number) => {
  if (item.type !== 'session') {
    const sessionIds = Array.isArray(item.sessions) ? item.sessions : [];
    return (
      <div
        key={`aggregate-${index}`}
        className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700"
      >
        <p>{item.content}</p>
        {sessionIds.length > 0 && (
          <div className="mt-3 space-y-2">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Related sessions
            </div>
            <div className="flex flex-wrap gap-2 text-xs text-slate-500">
              {sessionIds.slice(0, 5).map((sessionId) => (
                <Button
                  key={sessionId}
                  variant="outline"
                  size="sm"
                  className="border-slate-200"
                  asChild
                >
                  <Link to={`/analytics/sessions?sessionId=${encodeURIComponent(sessionId)}`}>
                    {sessionId.slice(0, 8)}
                  </Link>
                </Button>
              ))}
              {sessionIds.length > 5 && (
                <span className="text-[11px] font-medium text-slate-400">
                  +{sessionIds.length - 5} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  const sessionItem: OrgAskSessionEvidence = item;
  const qualificationLabel = sessionItem.booked_status === 'booked'
    ? 'Booked'
    : sessionItem.qualification_status === 'qualified'
      ? 'Qualified'
      : sessionItem.qualification_status === 'unqualified'
        ? 'Unqualified'
        : null;
  const metaBadges = [
    sessionItem.primary_intent ? { key: 'intent', label: sessionItem.primary_intent } : null,
    qualificationLabel ? { key: 'qualification', label: qualificationLabel } : null,
  ].filter(Boolean) as Array<{ key: string; label: string }>;

  const startedAt = formatEvidenceDate(sessionItem.started_at);
  const metrics = [
    sessionItem.friction_score !== undefined && sessionItem.friction_score !== null
      ? { key: 'friction', label: 'Friction', value: formatMetricValue(sessionItem.friction_score) }
      : null,
    sessionItem.rage_clicks !== undefined && sessionItem.rage_clicks !== null
      ? { key: 'rage', label: 'Rage clicks', value: formatMetricValue(sessionItem.rage_clicks) }
      : null,
    sessionItem.dead_clicks !== undefined && sessionItem.dead_clicks !== null
      ? { key: 'dead', label: 'Dead clicks', value: formatMetricValue(sessionItem.dead_clicks) }
      : null,
    sessionItem.backtracks !== undefined && sessionItem.backtracks !== null
      ? { key: 'backtracks', label: 'Backtracks', value: formatMetricValue(sessionItem.backtracks) }
      : null,
  ].filter(Boolean) as Array<{ key: string; label: string; value?: string }>;

  const sessionLink = `/analytics/sessions?sessionId=${encodeURIComponent(sessionItem.session)}`;

  return (
    <div
      key={sessionItem.session}
      className="rounded-xl border border-slate-200 bg-white p-4"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-3">
          <p className="text-sm text-slate-700">{sessionItem.content}</p>
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
            {metaBadges.map((badge) => (
              <Badge key={badge.key} variant="outline" className="bg-white/80 capitalize">
                {badge.label}
              </Badge>
            ))}
            {startedAt && (
              <span className="text-[11px] font-medium text-slate-400">{startedAt}</span>
            )}
          </div>
          {metrics.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {metrics.map((metric) => (
                <Badge
                  key={metric.key}
                  variant="secondary"
                  className="text-xs font-medium tracking-tight"
                >
                  {metric.label}: {metric.value}
                </Badge>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to={sessionLink}>Open Session</Link>
            </Button>
          </div>
          <span className="text-[11px] font-mono text-slate-400">{sessionItem.session}</span>
        </div>
      </div>
    </div>
  );
};

const AskHobbesPage: React.FC = () => {
  const { currentOrganization, isLoading: orgLoading } = useOrganization();
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [prompt, setPrompt] = useState('');
  const [selectedPreset, setSelectedPreset] = useState<string>('balanced');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [entries, setEntries] = useState<AskEntry[]>([]);
  const [hasHandledInitialPrompt, setHasHandledInitialPrompt] = useState(false);

  const systemPrompt = useMemo(() => {
    return tonePresets.find(t => t.value === selectedPreset)?.systemPrompt ?? tonePresets[0].systemPrompt;
  }, [selectedPreset]);

  const canSubmit = !!prompt.trim() && !!currentOrganization?.id && !isSubmitting;

  const createId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return `ask-${Date.now()}`;
  };

  const updateEntry = useCallback((id: string, data: Partial<AskEntry>) => {
    setEntries(prev => prev.map(entry => (entry.id === id ? { ...entry, ...data } : entry)));
  }, []);

  const handleSubmit = useCallback(async (value?: string) => {
    const activePrompt = (value ?? prompt).trim();
    if (!activePrompt || !currentOrganization?.id) return;

    const id = createId();
    const createdAt = new Date();

    setEntries(prev => [
      {
        id,
        prompt: activePrompt,
        createdAt,
        status: 'loading',
      },
      ...prev,
    ]);

    setPrompt('');
    setIsSubmitting(true);

    const payload: OrgAskRequest = {
      organizationId: currentOrganization.id,
      prompt: activePrompt,
      system_prompt: systemPrompt,
      ...buildClaudeOptions(),
    };

    let finalContent = '';
    let currentEvidence: OrgAskResponse['evidence'];
    let progress: StreamProgress | undefined;
    let streamFailed = false;

    const syncResponse = () =>
      updateEntry(id, {
        status: finalContent ? 'success' : 'loading',
        response: {
          content: finalContent || undefined,
          evidence: currentEvidence,
        },
        progress: progress ? { ...progress } : undefined,
      });

    const setProgress = (
      phase: StreamProgress['phase'],
      primaryMessage?: string,
      extraDetails?: string
    ) => {
      const randomFrom = (arr: string[]) => arr[Math.floor(Math.random() * arr.length)];
      const trimmedPrimary = primaryMessage?.trim();
      const trimmedExtra = extraDetails?.trim();
      const normalizedPrimary = trimmedPrimary?.toLowerCase();
      const normalizedExtra = trimmedExtra?.toLowerCase();
      const meaningfulPrimary =
        trimmedPrimary && (!normalizedPrimary || !GENERIC_STREAM_MESSAGES.has(normalizedPrimary))
          ? trimmedPrimary
          : undefined;
      const meaningfulExtra =
        trimmedExtra && (!normalizedExtra || !GENERIC_STREAM_MESSAGES.has(normalizedExtra))
          ? trimmedExtra
          : undefined;

      progress = {
        phase,
        headline: randomFrom(PROGRESS_HEADLINES[phase]),
        subline: meaningfulPrimary || meaningfulExtra || randomFrom(PROGRESS_SUBLINES[phase]),
        timestamp: Date.now(),
      };
      syncResponse();
    };

    const clearProgress = () => {
      if (progress) {
        progress = undefined;
        syncResponse();
      }
    };

    const markFailure = (message: string) => {
      streamFailed = true;
      progress = undefined;
      finalContent = '';
      updateEntry(id, {
        status: 'error',
        errorMessage: message,
        progress: undefined,
      });
      toast({
        variant: 'destructive',
        title: 'Ask Hobbes failed',
        description: message,
      });
    };

    try {
      const streamed = await streamAskResponse(payload, {
        onInitialContent: (text) => {
          setProgress('initial_content', text);
        },
        onToolUse: (event) => {
          setProgress('tool_use', event.message);
        },
        onToolResult: (event) => {
          if (event.status === 'error') {
            setProgress('tool_result', 'Query failed', event.message || 'The SQL query could not be executed.');
            return;
          }

          const sublineParts: string[] = [];
          if (event.row_count !== undefined && event.row_count !== null) {
            sublineParts.push(`Rows inspected: ${event.row_count}`);
          }
          if (event.result_preview && typeof event.result_preview === 'string') {
            sublineParts.push(event.result_preview);
          }
          setProgress('tool_result', event.message, sublineParts.length ? sublineParts.join(' · ') : undefined);
        },
        onFinalContent: (text) => {
          const trimmed = text.trim();
          const normalized = trimmed.toLowerCase();
          if (!GENERIC_STREAM_MESSAGES.has(normalized) && trimmed.length > 0) {
            finalContent = text;
          }
          clearProgress();
        },
        onEvidence: (items) => {
          if (Array.isArray(items)) {
            currentEvidence = [...(currentEvidence ?? []), ...items];
          } else if (items) {
            currentEvidence = [...(currentEvidence ?? []), items];
          }
          if (import.meta.env.DEV) {
            console.info('[AskHobbes] SSE evidence received', items);
          }
          syncResponse();
        },
        onError: (message) => {
          markFailure(message || 'Something went wrong while generating insights.');
        },
      });

      if (!streamFailed) {
        finalContent = streamed.content ?? finalContent;
        currentEvidence = streamed.evidence ?? currentEvidence;

        updateEntry(id, {
          status: 'success',
          response: {
            content: finalContent || undefined,
            evidence: currentEvidence,
          },
          progress: progress ? { ...progress } : undefined,
        });
      }
    } catch (error) {
      if (!streamFailed) {
        const message = error instanceof Error ? error.message : 'Unexpected error';
        updateEntry(id, { status: 'error', errorMessage: message, progress: undefined });
        toast({
          variant: 'destructive',
          title: 'Ask Hobbes failed',
          description: message,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [prompt, currentOrganization, systemPrompt, updateEntry, toast]);

  useEffect(() => {
    if (!currentOrganization?.id) {
      setEntries([]);
      return;
    }

    const cached = askHobbesCache.get(currentOrganization.id);
    if (cached) {
      setEntries(cloneEntries(cached));
    } else {
      setEntries([]);
    }
  }, [currentOrganization?.id]);

  useEffect(() => {
    if (!currentOrganization?.id) return;
    // store a shallow copy so future updates don't mutate cached objects
    askHobbesCache.set(currentOrganization.id, cloneEntries(entries));
  }, [entries, currentOrganization?.id]);

  // Handle initial prompt from URL query params (e.g., from dashboard navigation)
  useEffect(() => {
    const urlPrompt = searchParams.get('prompt');
    if (urlPrompt && currentOrganization?.id && !hasHandledInitialPrompt && !isSubmitting) {
      setHasHandledInitialPrompt(true);
      // Clear the query param from URL to prevent re-triggering
      setSearchParams({}, { replace: true });
      // Auto-submit the prompt
      handleSubmit(urlPrompt);
    }
  }, [searchParams, currentOrganization?.id, hasHandledInitialPrompt, isSubmitting, setSearchParams, handleSubmit]);

  const firstName = useMemo(() => {
    const displayName = user?.displayName?.trim();
    if (displayName) {
      return displayName.split(' ')[0] || 'there';
    }
    const email = user?.email;
    if (email) {
      const localPart = email.split('@')[0];
      return localPart || 'there';
    }
    return 'there';
  }, [user?.displayName, user?.email]);

  if (orgLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!currentOrganization) {
    return <div className="p-6 text-center text-muted-foreground">Select an organization to start asking Hobbes.</div>;
  }

  return (
    <div className="page-container mx-auto max-w-4xl py-10 space-y-8">
      <div className="space-y-8">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm p-8">
          <div className="flex items-center justify-between text-sm text-slate-500 mb-6">
            <span className="inline-flex items-center gap-2"><Sparkles className="h-4 w-4" /> Ask Hobbes</span>
            <Badge variant="outline" className="rounded-full">{currentOrganization.name}</Badge>
          </div>
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wide text-slate-400">Home</p>
              <h1 className="text-3xl font-semibold text-slate-900">Good morning, {firstName}!</h1>
              <p className="text-slate-600 max-w-2xl">
                Ask Hobbes anything about your sessions, friction, intents, or outcomes. We’ll stitch the story together so you see the full picture instantly.
              </p>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
              className="mt-6 space-y-5"
            >
              <div className="rounded-2xl border border-slate-200 bg-slate-50/60 px-5 py-4 shadow-inner">
                <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
                  <Search className="h-4 w-4" /> Ask Hobbes anything…
                </div>
                <Textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Where are we losing the most users this week?"
                  rows={4}
                  className="mt-3 border-none bg-transparent p-0 text-base shadow-none focus-visible:ring-0"
                />
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
                  <div className="flex items-center gap-3">
                    <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-slate-600">
                      <Wand2 className="h-3.5 w-3.5" /> Tone
                    </div>
                    <div className="inline-flex rounded-full bg-white p-1 shadow-inner">
                      {tonePresets.map((tone) => (
                        <button
                          key={tone.value}
                          type="button"
                          onClick={() => setSelectedPreset(tone.value)}
                          disabled={isSubmitting}
                          className={cn(
                            'px-3 py-1 text-xs font-medium rounded-full transition-colors',
                            selectedPreset === tone.value ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-700'
                          )}
                        >
                          {tone.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Button type="submit" disabled={!canSubmit} size="sm" className="rounded-full px-4">
                    {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {promptPresets.map((preset) => (
                  <Button
                    key={preset}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-full border-dashed bg-white"
                    onClick={() => handleSubmit(preset)}
                    disabled={isSubmitting}
                  >
                    {preset}
                  </Button>
                ))}
              </div>
            </form>
          </div>

        {entries.length === 0 ? (
          <Card className="border-dashed border-slate-200 bg-slate-50/70">
            <CardContent className="py-12 text-center text-slate-500 flex flex-col items-center gap-3">
              <Sparkles className="h-8 w-8 text-slate-400" />
              <p className="max-w-xl">No threads yet. Ask about friction hotspots, intent trends, or anything else you’d like Hobbes to investigate.</p>
            </CardContent>
          </Card>
        ) : (
          entries.map((entry) => {
            const progress = entry.progress;

            return (
              <Card key={entry.id} className="border border-slate-200/80">
                <CardHeader className="space-y-1">
                  <CardTitle className="text-base font-medium text-slate-900">{entry.prompt}</CardTitle>
                  <CardDescription className="text-xs text-slate-500">{entry.createdAt.toLocaleString()}</CardDescription>
                </CardHeader>
                <CardContent className="prose prose-slate max-w-none space-y-6 dark:prose-invert">
                  {entry.status === 'loading' && !progress && (
                    <div className="flex items-center gap-2 text-slate-500">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Thinking through your session signals…
                    </div>
                  )}
                  {progress && (
                    <div className="not-prose rounded-lg border border-slate-200 bg-slate-50/70 p-4 text-sm">
                      <div className="text-xs font-semibold tracking-wide text-slate-500">
                        {progress.headline}
                      </div>
                      {progress.subline && (
                        <div className="mt-2 text-slate-600">{progress.subline}</div>
                      )}
                    </div>
                  )}
                  {entry.status === 'error' && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600">
                      {entry.errorMessage ?? 'Something went wrong.'}
                    </div>
                  )}
                  {entry.status === 'success' && (
                    <ReactMarkdown
                      components={{
                        a: ({ node, href, ...props }) =>
                          href && href.startsWith('/') ? (
                            <Link to={href} {...props} />
                          ) : (
                            <a href={href} {...props} />
                          ),
                      }}
                    >
                      {prepareResponseContent(
                        entry.response?.content,
                        (entry.response?.evidence?.length ?? 0) > 0
                      ) || 'No answer returned.'}
                    </ReactMarkdown>
                  )}
                  {entry.status === 'success' && (entry.response?.evidence?.length ?? 0) > 0 && (
                    <div className="not-prose rounded-lg border border-slate-300 bg-slate-500/60 p-4">
                      <div className="text-sm font-semibold text-black">Evidence</div>
                      <div className="mt-3 space-y-2">
                        {entry.response?.evidence?.map((item, index) => renderEvidenceCard(item, index))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AskHobbesPage;
